/** Format cents to AUD currency string (e.g. 500 → "5.00", 125050 → "1,250.50") */
export function formatCurrency(cents: number): string {
  const dollars = cents / 100
  return dollars.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Parse a currency input string to cents. Strips commas and $ signs before parsing. Returns null for empty/invalid. */
export function parseCurrencyInput(value: string): number | null {
  const cleaned = value.replace(/[^0-9.]/g, '')
  if (cleaned === '') return null
  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) return null
  return Math.round(parsed * 100)
}

/**
 * Modifications value is sent to Entsia as whole dollars only; fractional dollars are rounded up.
 * @param cents Parsed cents from {@link parseCurrencyInput}
 */
export function modificationsValueWholeDollarsFromCents(cents: number): number {
  return Math.ceil(cents / 100)
}

function wholeDollarDigitsFromInput(value: string): string {
  const negative = value.startsWith('-')
  const noCurrency = value.replace(/[$,\s]/g, '')
  const integerPart = noCurrency.includes('.') ? noCurrency.slice(0, noCurrency.indexOf('.')) : noCurrency
  const digits = integerPart.replace(/[^0-9]/g, '')
  if (digits === '') return negative ? '-' : ''
  return negative ? `-${digits}` : digits
}

/** Parse a whole-dollar amount (no cents) to cents for internal use. */
export function parseWholeDollarCurrencyInput(value: string): number | null {
  const signedDigits = wholeDollarDigitsFromInput(value)
  if (signedDigits === '' || signedDigits === '-') return null
  const dollars = Number(signedDigits)
  if (!Number.isFinite(dollars)) return null
  return dollars * 100
}

/** Format whole-dollar input with thousands separators; ignores cents after a decimal point. */
export function formatWholeDollarInputValue(value: string): string {
  const signedDigits = wholeDollarDigitsFromInput(value)
  if (signedDigits === '' || signedDigits === '-') return signedDigits
  const dollars = Number(signedDigits)
  if (!Number.isFinite(dollars)) return ''
  return dollars.toLocaleString('en-AU', { maximumFractionDigits: 0 })
}

/** Format a raw value string for currency display: adds thousands separators, limits to 2 decimal places. */
export function formatInputValue(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '')
  if (cleaned === '') return ''
  const parts = cleaned.split('.')
  const integerPart = parts[0]
  const decimalPart = parts[1]
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  if (decimalPart !== undefined) {
    return `${formattedInteger}.${decimalPart.slice(0, 2)}`
  }
  return formattedInteger
}
